import { QueryClient } from '@tanstack/react-query';
import { screen, waitFor, within } from '@testing-library/react';
import { userEvent } from '@testing-library/user-event';
import { createRef, useMemo, type PropsWithChildren } from 'react';
import { beforeEach, describe, expect, it } from 'vitest';

import { render } from '@dynatrace/strato-components-testing/vitest';

import { Flex } from '../../layouts/flex/Flex.js';
import {
  EventNotificationMock,
  ResourceNotificationMock,
} from '../../notifications/notification-settings/__tests__/notification-endpoint-mocks.js';
import { NotificationQueryClientContext } from '../../notifications/notification-settings/hooks/notification-query-client.js';
import { ToastContainer } from '../../notifications/toast/ToastContainer.js';
import { Text } from '../../typography/text/Text.js';
import { NotifyButton } from './NotifyButton.js';

function Wrapper(props: Readonly<PropsWithChildren>) {
  // Create a new query client for each individual test run in order to avoid pollution
  // of the query cache from one test to the next.
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
          },
          mutations: {
            retry: false,
          },
        },
      }),
    [],
  );
  return (
    <NotificationQueryClientContext.Provider value={queryClient}>
      {props.children}
    </NotificationQueryClientContext.Provider>
  );
}

const customRender = (ui) => render(ui, { wrapper: Wrapper });

describe.each([
  ['resourceNotification', 'resource', ResourceNotificationMock],
  ['eventNotification', 'event', EventNotificationMock],
] as const)('NotificationConfig - %s', (name, type, MockClass) => {
  const notificationMock = new MockClass();
  beforeEach(() => {
    notificationMock.reset();
    notificationMock.list('empty-notifications');
    notificationMock.create('working');
    notificationMock.destroy('working');
  });

  it('sets className and style on the icon button', async () => {
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton
        className="test"
        style={{ backgroundColor: 'khaki' }}
        type={type}
        config={notificationMock.config}
      />,
    );

    const iconButton = await screen.findByRole('button');
    expect(iconButton).toHaveClass('test');
    expect(iconButton).toHaveStyle('background-color: rgb(240, 230, 140)');
  });

  it('provides a ref to the icon button', async () => {
    const ref = createRef<HTMLButtonElement>();
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton ref={ref} type={type} config={notificationMock.config} />,
    );

    const iconButton = await screen.findByRole('button');
    expect(iconButton).toEqual(ref.current);
  });

  it('should not override the data-dt-component attribute', async () => {
    const componentName = 'NotifyComponent';
    /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
    customRender(<NotifyButton data-dt-component={componentName} />);
    const iconButton = await screen.findByRole('button');
    expect(iconButton).toHaveAttribute('data-dt-component', 'NotifyButton');
  });

  it('should add data-dt-* attributes to the button', async () => {
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton
        data-dt-content="tracking"
        data-dt-name="MyNotifyButton"
        data-dt-children-name="NotifyChildren"
        type={type}
        config={notificationMock.config}
      />,
    );

    const iconButton = await screen.findByRole('button');
    expect(iconButton).toHaveAttribute('data-dt-content', 'tracking');
    expect(iconButton).toHaveAttribute('data-dt-name', 'MyNotifyButton');
    expect(iconButton).toHaveAttribute(
      'data-dt-children-name',
      'NotifyChildren',
    );
  });

  it('sets aria-label for the icon button', async () => {
    const label = 'My label';

    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton
        aria-label={label}
        type={type}
        config={notificationMock.config}
      />,
    );

    const iconButton = await screen.findByRole('button', { name: label });
    expect(iconButton).toBeInTheDocument();
  });

  it('should add data-dt-* attributes to NotifyButton.Content in the overlay', async () => {
    const user = userEvent.setup();

    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config}>
        <NotifyButton.Content
          data-dt-name="MyContent"
          data-dt-features="Feature1"
          data-dt-properties="key=value"
        >
          Custom content
        </NotifyButton.Content>
      </NotifyButton>,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);

    const content = document.querySelector('[data-dt-name="MyContent"]');
    expect(content).not.toBeNull();
    expect(content).toHaveAttribute('data-dt-features', 'Feature1');
    expect(content).toHaveAttribute('data-dt-properties', 'key=value');
  });

  it('sets data-dtrum on the icon button element', async () => {
    customRender(
      <>
        {/* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */}
        <NotifyButton
          data-dtrum-mask
          data-dtrum-allow
          type={type}
          config={notificationMock.config}
          aria-label="dtrum"
        />
        {/* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */}
        <NotifyButton
          type={type}
          config={notificationMock.config}
          aria-label="no dtrum"
        />
      </>,
    );

    const dtrum = await screen.findByRole('button', { name: 'dtrum' });
    expect(dtrum).toHaveAttribute('data-dtrum-allow', 'true');
    expect(dtrum).toHaveAttribute('data-dtrum-mask', 'true');

    const noDtrum = await screen.findByRole('button', {
      name: 'no dtrum',
    });
    expect(noDtrum).not.toHaveAttribute('data-dtrum-allow', 'true');
    expect(noDtrum).not.toHaveAttribute('data-dtrum-mask', 'true');
  });

  it('opens an overlay when clicking the icon button', async () => {
    notificationMock.list('has-notification');

    const user = userEvent.setup();
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config} />,
    );

    const iconButton = await screen.findByRole('button');
    expect(iconButton).toBeInTheDocument();
    expect(screen.queryByRole('dialog')).not.toBeInTheDocument();

    await user.click(iconButton);
    const overlay = screen.getByRole('dialog');

    expect(overlay).toHaveTextContent('Notifications are turned on');
    expect(overlay).toHaveTextContent(
      "You'll be notified about updates via email.",
    );
    expect(overlay).toHaveTextContent('Turn off notifications');
  });

  it('customRenders customRenders a button label when showLabel is true', async () => {
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config} showLabel />,
    );
    const button = await screen.findByRole('button');
    expect(button).toHaveTextContent('Notifications');
  });

  it('customRenders NotifyButton.Content within the overlay and discards children without slot', async () => {
    const user = userEvent.setup();

    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config}>
        <NotifyButton.Content>Test</NotifyButton.Content>
        should not customRender
      </NotifyButton>,
    );
    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    expect(screen.getByText('Test')).toBeInTheDocument();
    expect(
      screen.queryByText('should not customRender'),
    ).not.toBeInTheDocument();
  });

  it('provides access to NotifyButton.Content props', async () => {
    notificationMock.list('has-notification');
    const user = userEvent.setup();

    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config}>
        <NotifyButton.Content>
          {({ disabled, headingText, infoText }) => (
            <div data-testid="custom-customRender">
              <Flex flexDirection="column">
                <Text
                  data-testid="custom-heading"
                  style={disabled ? { color: 'red' } : { color: 'green' }}
                >
                  TEST: {headingText}
                </Text>
                <Text>TEST: {infoText}</Text>
              </Flex>
            </div>
          )}
        </NotifyButton.Content>
      </NotifyButton>,
    );
    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);

    const customHeading = screen.getByText('TEST: Notifications are turned on');
    expect(customHeading).toHaveStyle('color: rgb(0, 128, 0)');
    const customText = screen.getByText(
      "TEST: You'll be notified about updates via email.",
    );
    expect(customText).toBeInTheDocument();
  });

  it('can turn off notifications', async () => {
    notificationMock.list('has-notification');
    const user = userEvent.setup();
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config} />,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    const offButton = screen.getByRole('button', {
      name: 'Turn off notifications',
    });
    await user.click(offButton);

    expect(notificationMock.hasCalled('destroy')).toBe(true);
  });

  it('can turn on notifications', async () => {
    const user = userEvent.setup();
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config} />,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    const onButton = screen.getByRole('button', {
      name: 'Turn on notifications',
    });
    await user.click(onButton);

    expect(notificationMock.hasCalled('create')).toBe(true);
  });

  it("shows a toast and closes the overlay when user doesn't have permissions to create the notification", async () => {
    notificationMock.create('no-write-permission');

    const user = userEvent.setup();

    customRender(
      <>
        {/* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */}
        <NotifyButton type={type} config={notificationMock.config} />
        <ToastContainer />
      </>,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    const overlay = screen.getByRole('dialog');
    expect(overlay).toHaveTextContent('Notifications are turned off');

    const onButton = screen.getByRole('button', {
      name: 'Turn on notifications',
    });

    await user.click(onButton);
    expect(screen.queryByRole('dialog')).not.toBeInTheDocument();

    const toast = screen.getByRole('alert');
    expect(toast).toHaveTextContent('No permission to turn on notifications');
    expect(toast).toHaveTextContent(
      'Copy and share missing permissions with your admin.',
    );
    expect(
      within(toast).getByRole('button', { name: 'Copy missing permissions' }),
    ).toBeInTheDocument();
  });

  it("shows a toast and closes the overlay when user doesn't have permissions to delete the notification", async () => {
    notificationMock.list('has-notification');
    notificationMock.destroy('no-write-permission');

    const user = userEvent.setup();

    customRender(
      <>
        {/* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work  */}
        <NotifyButton type={type} config={notificationMock.config} />
        <ToastContainer />
      </>,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    const overlay = screen.getByRole('dialog');
    expect(overlay).toHaveTextContent('Notifications are turned on');

    let onButton;
    // Wait for the query to settle to the correct state before continuing.
    await waitFor(() => {
      onButton = screen.getByRole('button', {
        name: 'Turn off notifications',
      });
    });

    await user.click(onButton);
    expect(screen.queryByRole('dialog')).not.toBeInTheDocument();

    const toast = screen.getByRole('alert');
    expect(toast).toHaveTextContent('No permission to turn off notifications');
    expect(toast).toHaveTextContent(
      'Copy and share missing permissions with your admin.',
    );
    expect(
      within(toast).getByRole('button', { name: 'Copy missing permissions' }),
    ).toBeInTheDocument();
  });

  it('shows a toast and closes the overlay on generic errors', async () => {
    const user = userEvent.setup();

    notificationMock.create('server-error');

    customRender(
      <>
        {/* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */}
        <NotifyButton type={type} config={notificationMock.config} />
        <ToastContainer />
      </>,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    await user.click(
      screen.getByRole('button', { name: 'Turn on notifications' }),
    );
    expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
    expect(screen.getByRole('alert')).toHaveTextContent(
      'ErrorSomething went wrong. Please try again later.',
    );
  });

  it("disables the on-button and shows info, if user doesn't have read permissions", async () => {
    notificationMock.list('no-read-permission');

    const user = userEvent.setup();
    customRender(
      /* @ts-expect-error Due to the describe each, the type narrowing on the NotificationSettings does not work */
      <NotifyButton type={type} config={notificationMock.config} />,
    );

    const iconButton = await screen.findByRole('button');
    await user.click(iconButton);
    const onButton = screen.getByRole('button', {
      name: 'Turn on notifications',
    });
    expect(onButton).toBeDisabled();
    const infoButton = screen.getByRole('button', {
      name: 'Open additional information.',
    });
    await user.click(infoButton);
    expect(
      screen.getByRole('dialog', {
        name: 'No permission to turn on notifications Copy and share missing permissions with your admin: notification : notifications : read',
      }),
    ).toBeInTheDocument();
  });
});
